import { canSeeBuyFromSalesPartnerButton } from "~/features/salesPartnerStock/utils/salesPartnerStockHelper";
import { PDP_GA_NAME } from "~/features/search/models/SearchPageType";
import { BuyFromSalesPartnerButton } from "~/features/salesPartnerStock/components/BuyFromSalesPartnerButton";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Button, type ButtonProps } from "~/common/mui";
import { canSeeAddToCartButton, canSeeWishlistButton } from "~/utils/features";
import { isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { Trans } from "react-i18next";
import { userIsCustomer, userIsSalesman } from "~/domains/user/utils/roles";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import { trackConfigureAndQuoteClick, trackRequestAndQuoteClick } from "~/app/analytics/ga/ga";
import { EwellixRequestAQuoteButton } from "~/features/Ewellix/components/EwellixRequestAQuoteButton";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import DescriptionIcon from "@mui/icons-material/Description";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

type ThemeProps = Pick<ButtonProps, "color" | "variant">;
type ExternalButtonProps = Pick<ButtonProps, "onClick" | "disabled"> & {
  forceChangeToQuote?: boolean;
  quantity?: number;
};

const EWELLIX_BUTTONS = [
  {
    visible: (product: ProductDescriptor) => canSeeBuyFromSalesPartnerButton() && !isProductPMA(product),
    propsKey: "atcFromSP",
    render: renderBuyFromSalesPartnerButton,
  },
  {
    visible: (product: ProductDescriptor) => !isProductPMA(product) && canSeeAddToCartButton() && userIsCustomer(),
    propsKey: "atc",
    render: renderATCButton,
  },
  {
    visible: (product: ProductDescriptor) => {
      return !!product.base.lmbtConfigurator;
    },
    propsKey: "configurator",
    render: (product: ProductDescriptor, theme: ThemeProps, externalButtonProps: ExternalButtonProps) => (
      <Button
        key={"configurator"}
        size={"large"}
        startIcon={<BuildOutlinedIcon />}
        fullWidth={true}
        data-test-id={"configurator"}
        {...theme}
        {...externalButtonProps}
        onClick={(event) => {
          trackConfigureAndQuoteClick();
          externalButtonProps.onClick?.(event);
        }}
      >
        <Trans i18nKey={isProductPMA(product) ? "page.cart.button.configureAndQuote" : "page.cart.button.configure"} />
      </Button>
    ),
  },
  {
    visible: (product: ProductDescriptor) => {
      return isProductPMA(product);
    },
    propsKey: "requestAQuote",
    render: (product: ProductDescriptor, theme: ThemeProps, externalButtonProps: ExternalButtonProps) => (
      <EwellixRequestAQuoteButton
        key={"requestAQuote"}
        size={"large"}
        product={product.base}
        onClick={trackRequestAndQuoteClick}
        {...theme}
        {...externalButtonProps}
      />
    ),
  },
  {
    visible: (product: ProductDescriptor) => canSeeWishlistButton() && !isProductPMA(product),
    propsKey: "wishlist",
    render: renderWishlistButton,
  },
];
const SCHAEFFLER_BUTTONS = [
  {
    visible: () => canSeeBuyFromSalesPartnerButton(),
    propsKey: "atcFromSP",
    render: renderBuyFromSalesPartnerButton,
  },
  {
    visible: (product: ProductDescriptor) => canSeeAddToCartButton() && (userIsCustomer() || !productIsLinear(product)),
    propsKey: "atc",
    render: renderATCButton,
  },
  {
    visible: (product: ProductDescriptor) => productIsLinear(product),
    propsKey: "configurator",
    render: renderConfiguratorButton,
  },
  {
    visible: () => canSeeWishlistButton(),
    propsKey: "wishlist",
    render: renderWishlistButton,
  },
];

type PropsForButtons = {
  atc?: ExternalButtonProps;
  atcFromSP?: ExternalButtonProps & { quantity?: number };
  wishlist?: ExternalButtonProps;
  configurator?: ExternalButtonProps;
};

function getButtonTheme(index: number): ThemeProps {
  if (index === 0) {
    return { color: "primary" };
  } else if (index === 1) {
    return { color: "secondary" };
  } else {
    return { variant: "text" };
  }
}

export function renderButtons(product: ProductDescriptor, externalProps?: PropsForButtons) {
  const buttons = product.base.externalBrand ? EWELLIX_BUTTONS : SCHAEFFLER_BUTTONS;
  return buttons
    .filter(({ visible }) => visible(product))
    .map(({ render, propsKey }, index) => {
      const props: ExternalButtonProps =
        (propsKey ? externalProps?.[propsKey as keyof PropsForButtons] : undefined) ?? {};
      return render(product, getButtonTheme(index), props);
    });
}

function renderATCButton(
  product: ProductDescriptor,
  theme: ThemeProps,
  { forceChangeToQuote, ...externalButtonProps }: ExternalButtonProps,
) {
  let cartButtonLabel;
  let cartButtonIcon;
  let testId;
  if (userIsSalesman()) {
    testId = "pdp-add-to-quote";
    cartButtonLabel = "basket.addToQuote";
    cartButtonIcon = <DescriptionIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />;
  } else if ((userIsCustomer() && isProductPMA(product)) || forceChangeToQuote) {
    testId = "pdp-add-to-cart";
    cartButtonLabel = "cartPage.btn.quote";
    cartButtonIcon = <DescriptionIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />;
  } else {
    testId = "pdp-add-to-cart";
    cartButtonLabel = "addToCartPopup.add.submitBtn";
    cartButtonIcon = <ShoppingCartIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />;
  }

  return (
    <Button
      key={"buy"}
      size={"large"}
      startIcon={cartButtonIcon}
      fullWidth={true}
      data-test-id={testId}
      {...theme}
      {...externalButtonProps}
    >
      <Trans i18nKey={cartButtonLabel} />
    </Button>
  );
}

function renderConfiguratorButton(
  product: ProductDescriptor,
  theme: ThemeProps,
  externalButtonProps: ExternalButtonProps,
) {
  const url = product.base.configuratorUrl;
  return (
    <Button
      key={"configurator"}
      component={"a"}
      size={"large"}
      startIcon={<BuildOutlinedIcon />}
      fullWidth={true}
      data-test-id={"configurator"}
      {...theme}
      href={url}
      onClick={(event) => {
        // @ts-expect-error - html element mismatch - event is for Anchor, types are for Button
        externalButtonProps.onClick?.(event);
        trackConfigureAndQuoteClick();
      }}
    >
      <Trans i18nKey={"page.cart.button.configure"} />
    </Button>
  );
}

function renderWishlistButton(product: ProductDescriptor, theme: ThemeProps, externalButtonProps: ExternalButtonProps) {
  return (
    <Button
      key={"wishlist"}
      size={"large"}
      startIcon={<BookmarkIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
      fullWidth={true}
      data-test-id={"pdp-add-to-wishlist"}
      {...theme}
      {...externalButtonProps}
    >
      <Trans i18nKey={"basket.addToWishlist"} />
    </Button>
  );
}

function renderBuyFromSalesPartnerButton(
  product: ProductDescriptor,
  theme: ThemeProps,
  { quantity }: ExternalButtonProps,
) {
  return (
    <BuyFromSalesPartnerButton
      key={"buy-from-sales-partner"}
      size={"large"}
      fullWidth={true}
      data-test-id={"pdp-buy-from-sales-partner"}
      product={product.base}
      pageType={PDP_GA_NAME}
      quantity={quantity}
      {...theme}
    />
  );
}

function productIsLinear(product: ProductDescriptor) {
  return !!product.base.configuratorUrl;
}
